import { load, save } from '@/lib/storage';

export const CREATOR_VERSES_STORAGE_KEY = 'auctionCreatorVersesLocal';
export const CREATOR_VERSES_CHANGED_EVENT = 'anime-auction:creator-verses-changed';

export type CreatorRole = {
  id: string;
  name: string;
  locked?: boolean;
};

export type CreatorCharacter = {
  id: string;
  name: string;
  image_url?: string;
  tags?: string[];
  stats_by_role?: Record<string, Record<string, number> | number>;
};

export type CreatorVerseData = {
  meta: {
    baseVerseKey: string;
    theme: string;
    background_image_url?: string;
    auraFrom?: string;
    auraTo?: string;
  };
  roles: CreatorRole[];
  characters: CreatorCharacter[];
};

export type CreatorVerseRow = {
  id: string;
  verse_name: string;
  base_verse: string;
  data_json: CreatorVerseData;
  created_at: string;
  updated_at: string;
  // The public gallery entry this verse was submitted as, so a resubmit updates it.
  gallery_id?: string;
};

export const DEFAULT_CREATOR_ROLES: CreatorRole[] = [
  { id: 'role_captain', name: 'Captain', locked: true },
  { id: 'role_vice_captain', name: 'Vice Captain', locked: true },
  { id: 'role_support_1', name: 'Support 1', locked: true },
  { id: 'role_support_2', name: 'Support 2', locked: true },
  { id: 'role_tank', name: 'Tank', locked: true },
  { id: 'role_healer', name: 'Healer', locked: true },
];

const BASE_VERSES = new Set([
  'naruto',
  'bleach',
  'dragonball',
  'onepiece',
  'blackclover',
  'mha',
  'sololeveling',
  'jjk',
  '7deadlysins',
  'onepunchman',
  'demonslayer',
]);

const BASE_LABELS: Record<string, string> = {
  naruto: 'Naruto',
  bleach: 'Bleach',
  dragonball: 'Dragon Ball',
  onepiece: 'One Piece',
  blackclover: 'Black Clover',
  mha: 'My Hero Academia',
  sololeveling: 'Solo Leveling',
  jjk: 'Jujutsu Kaisen',
  '7deadlysins': '7 Deadly Sins',
  onepunchman: 'One Punch Man',
  demonslayer: 'Demon Slayer',
};

const BASE_BACKDROPS: Record<string, string> = {
  naruto: '/assets/backdrops/Naruto Backdrop.webp',
  bleach: '/assets/backdrops/Bleach Backdrop.webp',
  dragonball: '/assets/backdrops/Goku and Vegeta Backdrop.webp',
  onepiece: '/assets/backdrops/Onepiece Backdrop.webp',
  blackclover: '/assets/backdrops/blackclover Backdrop.webp',
  mha: '/assets/backdrops/mha Backdrop.webp',
  sololeveling: '/assets/backdrops/SoloLeveling Backdrop.webp',
  jjk: '/assets/backdrops/JJK Backdrop.webp',
  '7deadlysins': '/assets/backdrops/7DeadlySins Backdrop.webp',
  onepunchman: '/assets/backdrops/One Punch Man Backdrop.webp',
  demonslayer: '/assets/backdrops/DemonSlayer Backdrop.webp',
};

let runtimeCreatorVerseRows: CreatorVerseRow[] = [];

function makeId(prefix = 'creator') {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(2, 8)}`;
}

function nowIso() {
  return new Date().toISOString();
}

export function normalizeBaseVerseKey(value?: string | null) {
  const key = String(value || '').trim().toLowerCase();
  return BASE_VERSES.has(key) ? key : 'naruto';
}

export function getCreatorBaseVerseOptions() {
  return Array.from(BASE_VERSES).map((key) => ({
    key,
    label: BASE_LABELS[key] ?? key,
    backdrop: BASE_BACKDROPS[key] ?? BASE_BACKDROPS.naruto,
  }));
}

function notifyChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CREATOR_VERSES_CHANGED_EVENT));
  }
}

function normalizeRoleId(name: string) {
  const base = String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return base ? `role_${base}` : makeId('role');
}

function sanitizeDataJson(data: Partial<CreatorVerseData> | null | undefined, fallbackBaseVerse = 'naruto'): CreatorVerseData {
  const baseVerseKey = normalizeBaseVerseKey(data?.meta?.baseVerseKey || data?.meta?.theme || fallbackBaseVerse);
  const rawRoles = Array.isArray(data?.roles) ? data.roles : DEFAULT_CREATOR_ROLES;
  const roles: CreatorRole[] = [];
  const seenRoles = new Set<string>();

  rawRoles.forEach((role) => {
    const name = String(role?.name || '').trim();
    if (!name) return;
    const id = String(role?.id || normalizeRoleId(name)).trim();
    if (!id || seenRoles.has(id)) return;
    roles.push({ id, name, locked: Boolean(role?.locked) });
    seenRoles.add(id);
  });

  const characters = (Array.isArray(data?.characters) ? data.characters : []).map((character) => ({
    id: String(character?.id || makeId('char')),
    name: String(character?.name || '').trim(),
    image_url: String(character?.image_url || '').trim(),
    tags: Array.isArray(character?.tags) ? character.tags.map((tag) => String(tag)).filter(Boolean) : [],
    stats_by_role:
      character?.stats_by_role && typeof character.stats_by_role === 'object'
        ? character.stats_by_role
        : {},
  })).filter((character) => character.name);

  return {
    meta: {
      baseVerseKey,
      theme: normalizeBaseVerseKey(data?.meta?.theme || baseVerseKey),
      background_image_url: String(data?.meta?.background_image_url || BASE_BACKDROPS[baseVerseKey] || '').trim(),
      auraFrom: String(data?.meta?.auraFrom || '#f59e0b'),
      auraTo: String(data?.meta?.auraTo || '#ef4444'),
    },
    roles: roles.length ? roles : DEFAULT_CREATOR_ROLES,
    characters,
  };
}

function sanitizeRow(row: Partial<CreatorVerseRow> | null | undefined, index = 0): CreatorVerseRow {
  const createdAt = row?.created_at || nowIso();
  const baseVerse = normalizeBaseVerseKey(row?.base_verse || row?.data_json?.meta?.baseVerseKey);
  return {
    id: String(row?.id || makeId(`creator_${index}`)).trim(),
    verse_name: String(row?.verse_name || 'Creator Verse').trim() || 'Creator Verse',
    base_verse: baseVerse,
    data_json: sanitizeDataJson(row?.data_json, baseVerse),
    created_at: createdAt,
    updated_at: row?.updated_at || createdAt,
    ...(row?.gallery_id ? { gallery_id: String(row.gallery_id) } : {}),
  };
}

function saveRows(rows: CreatorVerseRow[]) {
  save(CREATOR_VERSES_STORAGE_KEY, rows);
  notifyChanged();
}

export function loadCreatorVerseRows(): CreatorVerseRow[] {
  const raw = load<CreatorVerseRow[]>(CREATOR_VERSES_STORAGE_KEY, []);
  return (Array.isArray(raw) ? raw : []).map((row, index) => sanitizeRow(row, index));
}

export function loadRuntimeCreatorVerseRows(): CreatorVerseRow[] {
  return runtimeCreatorVerseRows;
}

export function registerRuntimeCreatorVerse(row: CreatorVerseRow) {
  const normalized = sanitizeRow(row);
  runtimeCreatorVerseRows = [
    normalized,
    ...runtimeCreatorVerseRows.filter((entry) => entry.id !== normalized.id),
  ];
  notifyChanged();
  return normalized;
}

export function getCreatorVerseRow(id: string | null | undefined) {
  if (!id) return null;
  const normalizedId = String(id).trim().toLowerCase();
  return loadCreatorVerseRows().find((row) => row.id.toLowerCase() === normalizedId) ?? null;
}

export function createCreatorVerseRow({ baseVerseKey = 'naruto', verseName }: { baseVerseKey?: string; verseName?: string } = {}) {
  const baseVerse = normalizeBaseVerseKey(baseVerseKey);
  const now = nowIso();
  const row = sanitizeRow({
    id: makeId('creator'),
    verse_name: verseName || `${BASE_LABELS[baseVerse] ?? 'Custom'} Creator Verse`,
    base_verse: baseVerse,
    data_json: sanitizeDataJson(null, baseVerse),
    created_at: now,
    updated_at: now,
  });
  const rows = loadCreatorVerseRows();
  rows.push(row);
  saveRows(rows);
  return row;
}

export function updateCreatorVerseRow(id: string, patch: Partial<CreatorVerseRow>) {
  const rows = loadCreatorVerseRows();
  const index = rows.findIndex((row) => row.id === id);
  if (index < 0) return null;
  const current = rows[index];
  const next = sanitizeRow({
    ...current,
    ...patch,
    id: current.id,
    created_at: current.created_at,
    updated_at: nowIso(),
  }, index);
  rows[index] = next;
  saveRows(rows);
  return next;
}

export function deleteCreatorVerseRow(id: string) {
  const rows = loadCreatorVerseRows();
  const next = rows.filter((row) => row.id !== id);
  if (next.length === rows.length) return false;
  saveRows(next);
  return true;
}

export function buildCreatorVerseExport(row: CreatorVerseRow) {
  // The gallery link belongs to this browser; whoever imports the file submits their own.
  const { gallery_id: _galleryId, ...safeRow } = sanitizeRow(row);
  return {
    version: 1,
    exported_at: nowIso(),
    type: 'anime-auction-creator-verse',
    verse: safeRow,
  };
}

export function importCreatorVerseFromText(text: string) {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error('Could not read that JSON file.');
  }

  const payload = parsed as { type?: string; verse?: Partial<CreatorVerseRow> } & Partial<CreatorVerseRow>;
  const source = payload.type === 'anime-auction-creator-verse' && payload.verse ? payload.verse : payload;
  if (!source || typeof source !== 'object') {
    throw new Error('Unsupported creator verse file.');
  }

  const now = nowIso();
  const row = sanitizeRow({
    ...source,
    id: makeId('creator'),
    created_at: now,
    updated_at: now,
  });
  const rows = loadCreatorVerseRows();
  rows.push(row);
  saveRows(rows);
  return row;
}
